import React, { useState } from "react";
import {
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import type { PaymentIntentResult, SetupIntentResult } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import { Button } from "../misc/Button";
import StepProgressBar from "../progress-bar";
import logo from "../../assets/images/logo.png";
import { auth } from "../../firebase/firebase-config";
import { getIdToken } from "firebase/auth";
import axios from "axios";
import LoadingScreen from "../../screens/LoadingScreen";
import { Card, CardContent } from "../misc/Card";
import Divider from "../misc/Divider";
import { Link } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import SimpleTooltip from "../misc/SimpleTooltip";
import { getAuthHeader } from "../../utils/authHeader";

interface PaymentFormProps {
    advanceStep: () => void;
    goBackStep: () => void;
    isOnboarding: boolean;
    details: {
        name: string;
        price: number;
        amountDue: number;
        nextDate: number;
        discount: number;
        promoDuration?: {
            type: string;
            months: number | null;
        };
        trailDays?: number | null;
        freeTrial?: boolean | null;
        prorationAmount?: number | null;
        isMinimumChargeApplied?: boolean;
    };
    refresh?: boolean;
    promoCode: string;
    setPromoCode: (promo: string) => void;
    applyPromo: () => void;
    removePromo: () => void;
    promoStatus: -1 | 0 | 1; // -1: invalid, 0: no promoCode, 1: valid
    subscriptionId: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
    advanceStep,
    goBackStep,
    isOnboarding,
    details,
    refresh = false,
    promoCode,
    setPromoCode,
    applyPromo,
    removePromo,
    promoStatus,
    subscriptionId,
}) => {
    // Use our custom hook to determine if the screen is mobile (e.g. width <= 700px)
    const isMobile = useIsMobile(700);

    const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const stripe = useStripe();
    const elements = useElements();

    const handleBack = () => {
        goBackStep();
    };

    const handleNext = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            toast.error(
                <CustomToast
                    message="Stripe.js has not yet loaded. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return;
        }

        setIsProcessing(true);

        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = await getAuthHeader();

            await axios.get(`${backendUrl}/onboarding/tos-accept`, { headers });
        } catch (error) {
            console.error("Error processing payment:", error);
        }
        let result:
            | PaymentIntentResult
            | SetupIntentResult
            | Awaited<ReturnType<typeof stripe.confirmSetup>>;

        if (details.freeTrial) {
            // we only need to collect & attach the card for off-session use
            result = await stripe.confirmSetup({
                elements,
                confirmParams: {},
                redirect: "if_required",
            });
        } else {
            // normal pay flow
            result = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    /* optional return_url */
                },
                redirect: "if_required",
            });
        }
        console.log("Payment result:", result);
        if (result.error) {
            toast.error(
                <CustomToast
                    message={`Payment failed: ${result.error.message}`}
                    type="error"
                />
            );
            setIsProcessing(false);
            return;
        } else {
            toast.success(
                <CustomToast message="Payment succeeded!" type="success" />,
                { autoClose: 3000 }
            );

            try {
                const currentUser = auth.currentUser;
                if (!currentUser) throw new Error("User not authenticated");
                const token = await getIdToken(currentUser, true);
                const backendUrl = process.env.REACT_APP_BACKEND_URL;

                if (details.freeTrial) {
                    const activateTrialResponse = await axios.post(
                        `${backendUrl}/onboarding/activate-free-trial`,
                        {
                            code: promoCode,
                            subscriptionId,
                        },
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );

                    console.log(
                        "Activate trial response:",
                        activateTrialResponse
                    );

                    if (activateTrialResponse.status !== 200) {
                        throw new Error("Failed to activate free trial");
                    }

                    toast.success(
                        <CustomToast
                            message="Free trial activated successfully!"
                            type="success"
                        />,
                        { autoClose: 3000 }
                    );
                }

                const response = await axios.get(
                    `${backendUrl}/onboarding/activate-user`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                if (isOnboarding) {
                    await axios
                        .get(
                            `${backendUrl}/onboarding/send-verification-email`,
                            {
                                headers: { Authorization: `Bearer ${token}` },
                            }
                        )
                        .catch((error: Error) => {
                            console.error(error);
                        });
                }

                if (response.status === 200) {
                    toast.success(
                        <CustomToast
                            message="Account activated successfully!"
                            type="success"
                        />,
                        { autoClose: 3000 }
                    );

                    if (response.data || refresh) {
                        window.location.reload();
                    } else {
                        advanceStep();
                    }
                }
            } catch (error) {
                console.error("Error activating account:", error);
                toast.error(
                    <CustomToast
                        message="Account activation failed. Please contact support."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            } finally {
                setIsProcessing(false);
            }
        }
    };

    if (!stripe || !elements) {
        return <LoadingScreen />;
    }

    function formatPromoPeriod(startUnix: number, months: number): string {
        const start = new Date(startUnix * 1000);
        // Normalize to the 1st of the month to avoid date rollover issues (e.g., Jan 31 + 1 month = Mar 2)
        const normalized = new Date(start.getFullYear(), start.getMonth(), 1);
        
        const opts: Intl.DateTimeFormatOptions = {
            month: "long",
            year: "numeric",
        };
        const startLabel = normalized.toLocaleDateString("en-US", opts);

        if (months === 1) {
            return startLabel;
        }
        if (months === 2) {
            const second = new Date(normalized);
            second.setMonth(second.getMonth() + 1);
            const secondLabel = second.toLocaleDateString("en-US", opts);
            return `${startLabel} and ${secondLabel}`;
        }
        // 3 or more
        const end = new Date(normalized);
        end.setMonth(end.getMonth() + months - 1);
        const endLabel = end.toLocaleDateString("en-US", opts);
        return `${startLabel} to ${endLabel}`;
    }

    function getEndOfPromoDate(startDateUnix: number, months: number): Date {
        const raw = new Date(startDateUnix * 1000 + 86400000);
        raw.setMonth(raw.getMonth() + months);
        return new Date(raw.getFullYear(), raw.getMonth(), 1);
    }

    function getLastPromoDay(startDateUnix: number, months: number): Date {
        const resume = new Date(startDateUnix * 1000 + 86400000);
        resume.setMonth(resume.getMonth() + months);
        return new Date(resume.getTime() - 86400000);
    }

    console.log("details", details);

    return (
        <main
            className={`flex flex-col justify-center items-center min-h-screen bg-background shadow-[0px_4px_4px_rgba(0,0,0,0.25)] ${
                isMobile ? "overflow-y-auto" : "overflow-hidden"
            }`}
        >
            <section
                className={`flex flex-col items-center px-8 py-20 bg-white ${
                    isMobile
                        ? "w-full min-h-full pb-24"
                        : "w-[905px] min-h-[833px] rounded-2xl"
                }`}
            >
                <div className="w-full flex flex-col items-center mb-8">
                    <img
                        loading="lazy"
                        src={logo}
                        alt="Agent Registration Logo"
                        className="object-contain self-center max-w-full aspect-[8.77] w-[395px]"
                    />
                    <h1 className="my-2 text-primary text-2xl font-bold leading-none text-center">
                        {isOnboarding
                            ? "User Onboarding"
                            : "Resubscribe to Marabot"}
                    </h1>
                    {isOnboarding && (
                        <StepProgressBar
                            steps={[
                                {
                                    label: "Agent Details",
                                    isActive: true,
                                    isCompleted: true,
                                },
                                {
                                    label: "Broker Details",
                                    isActive: true,
                                    isCompleted: true,
                                },
                                {
                                    label: "Choose MLS",
                                    isActive: true,
                                    isCompleted: true,
                                },
                                {
                                    label: "Select Plan",
                                    isActive: true,
                                    isCompleted: true,
                                },
                                {
                                    label: "Payment",
                                    isActive: true,
                                    isCompleted: false,
                                },
                            ]}
                        />
                    )}
                </div>

                <div
                    className={`w-full ${
                        isMobile
                            ? "flex flex-col space-y-4"
                            : "flex justify-center space-x-4"
                    }`}
                >
                    <Card
                        className={`${
                            isMobile ? "w-full" : "w-[40%] self-start"
                        }`}
                    >
                        <CardContent className="flex flex-col items-start space-y-2 mb-4">
                            <h2 className="text-xl font-bold text-primary">
                                Payment Information:
                            </h2>
                            <div className="text-neutral-500 text-base space-y-2">
                                <p>
                                    <span className="text-primary font-semibold">
                                        Plan:
                                    </span>
                                    {` ${details.name}`}
                                </p>
                                <p>
                                    <span className="text-primary font-semibold">
                                        Regular Price:
                                    </span>
                                    {` $${details.price / 100} a month`}
                                </p>
                                {promoStatus === 1 &&
                                    details.freeTrial !== true && (
                                        <p>
                                            <span className="text-primary font-semibold">
                                                Promo Code Discount
                                            </span>
                                            {` -$${(
                                                details.discount / 100
                                            ).toFixed(2)}`}
                                        </p>
                                    )}
                                {details.price -
                                    details.amountDue -
                                    (details.discount || 0) >
                                    0 && (
                                    <p>
                                        <span className="text-primary font-semibold">
                                            Prorated Discount:
                                        </span>
                                        {` -$${(
                                            (details.price -
                                                details.amountDue -
                                                (details.discount || 0)) /
                                            100
                                        ).toFixed(2)}`}
                                    </p>
                                )}
                                <Divider />
                                {details.isMinimumChargeApplied && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 my-2">
                                        <p className="text-blue-800 text-sm">
                                            <span className="font-semibold">
                                                ℹ️ Minimum Charge Applied:
                                            </span>{" "}
                                            A minimum charge of $1.00 has been applied to activate your subscription.
                                        </p>
                                    </div>
                                )}
                                <p>
                                    <span className="text-primary font-semibold">
                                        Total Due Today:
                                    </span>
                                    {` $${(details.amountDue / 100).toFixed(
                                        2
                                    )}`}
                                </p>

                                {!!(
                                    promoStatus === 1 && details?.discount > 0
                                ) && (
                                    <p>
                                        Your promo code{" "}
                                        <span className="font-semibold">
                                            {promoCode}
                                        </span>{" "}
                                        gives you{" "}
                                        <span className="font-semibold">
                                            ${details.discount / 100} off for{" "}
                                            {details.promoDuration?.type ===
                                                "repeating" &&
                                            details.promoDuration.months
                                                ? formatPromoPeriod(
                                                      details.nextDate,
                                                      details.promoDuration
                                                          .months
                                                  )
                                                : "forever"}
                                            .
                                        </span>
                                    </p>
                                )}

                                <p className="text-primary font-semibold">
                                    Next Payment:
                                </p>

                               {details.freeTrial ? (
                                    <>
                                        <p>
                                            On{" "}
                                            <span className="font-semibold">
                                                {new Date(
                                                    details.nextDate * 1000
                                                ).toLocaleDateString("en-US", {
                                                    month: "long",
                                                    day: "numeric",
                                                    year: "numeric",
                                                })}
                                            </span>
                                            , your subscription will renew at{" "}
                                            <span className="font-semibold">
                                                $
                                                {(
                                                    (details?.prorationAmount ||
                                                        0) / 100
                                                ).toFixed(2)}
                                            </span>
                                            .
                                        </p>

                                        <p>
                                            For{" "}
                                            <span className="font-semibold">
                                                {new Date(
                                                    // first of next month after details.nextDate
                                                    new Date(
                                                        details.nextDate * 1000
                                                    ).getFullYear(),
                                                    new Date(
                                                        details.nextDate * 1000
                                                    ).getMonth() + 1,
                                                    1
                                                ).toLocaleDateString("en-US", {
                                                    month: "long",
                                                    year: "numeric",
                                                })}
                                            </span>
                                            , your subscription will renew at{" "}
                                            <span className="font-semibold">
                                                $
                                                {(details.price / 100).toFixed(
                                                    2
                                                )}
                                            </span>{" "}
                                            a month.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        {(details.promoDuration?.type ===
                                            "repeating" &&
                                            details.promoDuration.months &&
                                            details.promoDuration.months ===
                                                2) ||
                                        details.promoDuration?.type ===
                                            "forever" ? (
                                            <p>
                                                Your subscription will renew on{" "}
                                                <span className="font-semibold">
                                                    {new Date(
                                                        details.nextDate * 1000
                                                    ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            month: "long",
                                                            day: "numeric",
                                                            year: "numeric",
                                                        }
                                                    )}
                                                </span>{" "}
                                                for{" "}
                                                <span className="font-semibold">
                                                    {(details.promoDuration
                                                        ?.type ===
                                                        "repeating" &&
                                                        details.promoDuration
                                                            .months &&
                                                        details.promoDuration
                                                            .months > 1) ||
                                                    details.promoDuration
                                                        ?.type === "forever"
                                                        ? `$${Math.max(0,(
                                                              (details.price -
                                                                  details.discount) /
                                                              100
                                                          )).toFixed(2)}`
                                                        : `$${(
                                                              details.price /
                                                              100
                                                          ).toFixed(2)}`}{" "}
                                                    a month.
                                                </span>
                                            </p>
                                        ) : details.promoDuration?.type ===
                                              "repeating" &&
                                          details.promoDuration.months &&
                                          details.promoDuration.months > 2 ? (
                                            <p>
                                                From{" "}
                                                <span className="font-semibold">
                                                    {new Date(
                                                        details.nextDate *
                                                            1000 +
                                                            86400000
                                                    ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            month: "long",
                                                            year: "numeric",
                                                        }
                                                    )}{" "}
                                                </span>
                                                to{" "}
                                                <span className="font-semibold">
                                                    {getLastPromoDay(
                                                        details.nextDate,
                                                        details.promoDuration
                                                            .months - 1
                                                    ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            month: "long",
                                                            year: "numeric",
                                                        }
                                                    )}{" "}
                                                </span>
                                                , your subscription will renew
                                                at{" "}
                                                <span className="font-semibold">
                                                    $
                                                    {(details.price -
                                                        details.discount) /
                                                        100}{" "}
                                                    a month.
                                                </span>
                                            </p>
                                        ) : details.promoDuration?.type ===
                                              "repeating" &&
                                          details.promoDuration.months &&
                                          details.promoDuration.months === 1 ? (
                                            <></>
                                        ) : (
                                            <p>
                                                Your subscription will renew on{" "}
                                                <span className="font-semibold">
                                                    {new Date(
                                                        details.nextDate * 1000
                                                    ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            month: "long",
                                                            day: "numeric",
                                                            year: "numeric",
                                                        }
                                                    )}
                                                </span>{" "}
                                                at{" "}
                                                <span className="font-semibold">
                                                    {(details.promoDuration
                                                        ?.type ===
                                                        "repeating" &&
                                                        details.promoDuration
                                                            .months &&
                                                        details.promoDuration
                                                            .months > 1) ||
                                                    details.promoDuration
                                                        ?.type === "forever"
                                                        ? `$${(
                                                              (details.price -
                                                                  details.discount) /
                                                              100
                                                          ).toFixed(2)}`
                                                        : `$${(
                                                              details.price /
                                                              100
                                                          ).toFixed(2)}`}{" "}
                                                    a month.
                                                </span>
                                            </p>
                                        )}
                                    </>
                                )}
                                {promoStatus === 1 &&
                                    !details.isMinimumChargeApplied &&
                                    details.promoDuration?.type ===
                                        "repeating" &&
                                    details.promoDuration.months &&
                                    (details.promoDuration.months === 1 ? (
                                        <p>
                                            Your subscription will be renewed on{" "}
                                            <span className="font-semibold">
                                                {new Date(
                                                    details.nextDate * 1000
                                                ).toLocaleDateString("en-US", {
                                                    month: "long",
                                                    day: "numeric",
                                                    year: "numeric",
                                                })}
                                            </span>
                                            , with the regular pricing of{" "}
                                            <span className="font-semibold">
                                                $
                                                {(details.price / 100).toFixed(
                                                    2
                                                )}{" "}
                                                a month.
                                            </span>
                                        </p>
                                    ) : (
                                        details.promoDuration.months > 1 && (
                                            <p>
                                                From{" "}
                                                <span className="font-semibold">
                                                    {getEndOfPromoDate(
                                                        details.nextDate,
                                                        details.promoDuration
                                                            .months - 1
                                                    ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            month: "long",
                                                            year: "numeric",
                                                        }
                                                    )}
                                                </span>
                                                , regular pricing of{" "}
                                                <span className="font-semibold">
                                                    $
                                                    {(
                                                        details.price / 100
                                                    ).toFixed(2)}{" "}
                                                    a month
                                                </span>{" "}
                                                will resume.
                                            </p>
                                        )
                                    ))}
                                {promoStatus === 1 &&
                                    details.isMinimumChargeApplied &&
                                    details.promoDuration?.type === "repeating" &&
                                    details.promoDuration.months && 
                                    details.promoDuration.months > 0 && (
                                        <p>
                                            From{" "}
                                            <span className="font-semibold">
                                                {getEndOfPromoDate(
                                                    details.nextDate,
                                                    details.promoDuration.months - 1
                                                ).toLocaleDateString("en-US", {
                                                    month: "long",
                                                    year: "numeric",
                                                })}
                                            </span>
                                            , regular pricing of{" "}
                                            <span className="font-semibold">
                                                ${(details.price / 100).toFixed(2)} a month
                                            </span>{" "}
                                            will resume.
                                        </p>
                                    )}
                            </div>

                            {isOnboarding && (
                                <>
                                    <div className="mb-4">
                                        <label
                                            htmlFor="promo"
                                            className="block text-sm font-medium text-gray-700"
                                        >
                                            Promo Code
                                        </label>
                                        <input
                                            id="promo"
                                            type="text"
                                            value={promoCode}
                                            onChange={(e) =>
                                                setPromoCode(e.target.value)
                                            }
                                            className="mt-1 block w-full border rounded px-2 py-1"
                                            placeholder="MARABOT2025"
                                            disabled={promoStatus === 1}
                                        />
                                    </div>
                                    {promoStatus !== 1 ? (
                                        <Button
                                            type="button"
                                            onClick={applyPromo}
                                            className="bg-primary text-white rounded px-4 py-2"
                                        >
                                            Apply
                                        </Button>
                                    ) : (
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            onClick={removePromo}
                                        >
                                            Remove
                                        </Button>
                                    )}

                                    {promoStatus === 1 && (
                                        <p className="text-sm text-green-600 mt-2">
                                            Promo code applied successfully!
                                        </p>
                                    )}

                                    {promoStatus === -1 && (
                                        <p className="text-sm text-red-600 mt-2">
                                            Invalid / Expired promo code. Please
                                            try again.
                                        </p>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Card className={`${isMobile ? "w-full" : "w-2/3"}`}>
                        <CardContent>
                            <div className="flex-grow flex items-center w-full">
                                <form onSubmit={handleNext} className="w-full">
                                    <div className="w-full">
                                        <PaymentElement />
                                    </div>
                                    {isProcessing && <LoadingScreen />}
                                    <div
                                        className={`w-full mt-8 ${
                                            isMobile
                                                ? "flex flex-col gap-4"
                                                : "flex justify-between gap-20"
                                        }`}
                                    >
                                        <Button
                                            className={`${
                                                isMobile ? "w-full" : "w-1/3"
                                            }`}
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <div>
                                            <SimpleTooltip
                                                message="Please accept the terms and conditions to proceed."
                                                visible={
                                                    !acceptedTerms || false
                                                }
                                            >
                                                <Button
                                                    type="submit"
                                                    disabled={
                                                        !stripe ||
                                                        !elements ||
                                                        isProcessing ||
                                                        !acceptedTerms
                                                    }
                                                    className={`${
                                                        isMobile ? "w-full" : ""
                                                    }`}
                                                >
                                                    {isProcessing
                                                        ? "Processing..."
                                                        : "Pay"}
                                                </Button>
                                            </SimpleTooltip>
                                        </div>
                                    </div>

                                    {/* Terms and Conditions */}
                                    <div className="flex items-center justify-center mt-6">
                                        <input
                                            type="checkbox"
                                            id="terms"
                                            checked={acceptedTerms}
                                            onChange={(e) =>
                                                setAcceptedTerms(
                                                    e.target.checked
                                                )
                                            }
                                            className="mr-2 accent-primary hover:accent-secondary"
                                        />
                                        <label
                                            htmlFor="terms"
                                            className="text-sm text-neutral-500"
                                        >
                                            I agree to the{" "}
                                            <Link
                                                to="https://marabot.ai/terms"
                                                className="text-primary hover:text-secondary cursor-pointer"
                                                target="_blank"
                                            >
                                                Terms and Conditions
                                            </Link>
                                        </label>
                                    </div>
                                </form>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-10 text-neutral-500 text-sm text-center">
                    {details.freeTrial &&
                        details.trailDays !== null &&
                        details.trailDays !== undefined && (
                            <p className="mb-2 text-primary">
                                We’ll place a temporary $1 charge to verify your
                                card — you won’t be billed until the{" "}
                                {details.trailDays} day trial ends.
                            </p>
                        )}
                    <p className="mb-2">
                        Marabot subscriptions are billed monthly in advance.
                    </p>
                    *Charges will appear as ISPEAK.AI, our corporate entity. For
                    questions, contact{" "}
                    <Link
                        to="mailto://support@marabot.ai"
                        className="text-primary hover:text-secondary cursor-pointer"
                        target="_blank"
                    >
                        support@marabot.ai
                    </Link>
                </div>
            </section>
        </main>
    );
};

export default PaymentForm;
